import {GLOBAL} from "../../../common/globals";
import type {EopPopover} from "./popover";
import {Resolution} from "../../../common/resolution";
import {resolve} from "../../../container";
import {Page} from "../../../common/page";
import {eventOccurredOutside} from "../../../common/utils/events";
import {BOOTSTRAP} from "../../../common/resolutionConstants";


const POPOVER_ABOVE_CLASS = "popover-above";
const POPOVER_BELOW_CLASS = "popover-below";
const POPOVER_ON_LEFT_EDGE_CLASS = "popover-on-left-edge";
const POPOVER_ON_RIGHT_EDGE_CLASS = "popover-on-right-edge";
const POPOVER_IN_CENTER = "popover-in-center";

//has to be in sync with the scss variable $popover-arrow-height
const POPOVER_ARROW_HEIGHT = 11;

export interface PopoverDisplayMode {
    open: () => void;

    close: () => void;

    toggle: () => void;
}


export abstract class PresentDisplayMode implements PopoverDisplayMode {
    protected outsideClickHandler: (event: Event) => void;

    protected constructor(protected element: EopPopover) {
        this.outsideClickHandler = () => {
        };
    }

    public abstract adjustPopoverOffset(): void;

    public open(): void {
        if (this.isOpen()) {
            return;
        }

        this.element.silentlyShowAfter(() => this.adjustPopoverOffset());

        GLOBAL.bodyElement().addEventListener("click", this.outsideClickHandler);
        GLOBAL.bodyElement().addEventListener("touchstart", this.outsideClickHandler);
        this.element.popoverContainer.addEventListener("click", this.outsideClickHandler);
        this.element.popoverContainer.addEventListener("touchstart", this.outsideClickHandler);
    }

    public close(): void {
        if (!this.isOpen()) {
            return;
        }
        GLOBAL.bodyElement().removeEventListener("click", this.outsideClickHandler);
        GLOBAL.bodyElement().removeEventListener("touchstart", this.outsideClickHandler);
        this.element.popoverContainer.removeEventListener("click", this.outsideClickHandler);
        this.element.popoverContainer.removeEventListener("touchstart", this.outsideClickHandler);
        this.element.doHide();
    }

    public toggle(): void {
        if (this.isOpen()) {
            this.close();
        } else {
            this.open();
        }
    }

    protected isOpen(): boolean {
        return this.element.active;
    }

}

export class AbsentDisplayMode implements PopoverDisplayMode {


    public open(): void {
    }

    public close(): void {
    }

    public toggle(): void {
    }

}

const ADDITIONAL_TOOLTIP_PADDING_MOBILE = 10;
const ADDITIONAL_TOOLTIP_PADDING_DESKTOP = 25;

export class TooltipDisplayMode extends PresentDisplayMode {

    private popoverHeight: number = 0;
    private popoverWidth: number = 0;

    public constructor(
        popover: EopPopover,
        private resolution: Resolution = resolve(Resolution),
        private page: Page = resolve(Page)
    ) {
        super(popover);

        if (this.isOpen()) {
            this.adjustPopoverOffset();
        }
        this.outsideClickHandler = event => {
            if (eventOccurredOutside(event, this.element.managingParent!) && eventOccurredOutside(event, this.element)) {
                this.close();
                event.stopPropagation();
            }
        };
    }

    public adjustPopoverOffset(): void {
        this.resetLayout();
        this.setAdditionalPadding();
        this.setPopoverHeight();
        this.setPopoverWidth();
        this.adjustHorizontalOffset();
        this.adjustVerticalOffset();
    }

    private resetLayout(): void {
        [
            POPOVER_ABOVE_CLASS,
            POPOVER_BELOW_CLASS,
            POPOVER_IN_CENTER,
            POPOVER_ON_LEFT_EDGE_CLASS,
            POPOVER_ON_RIGHT_EDGE_CLASS
        ].forEach(
            className => this.element.popoverContainer.classList.remove(className)
        );
        this.element.popoverElement.style.left = "";
    }

    private adjustHorizontalOffset(): void {
        if (this.isOnLeftEdge()) {
            this.element.popoverContainer.classList.add(POPOVER_ON_LEFT_EDGE_CLASS);
        } else if (this.isOnRightEdge()) {
            this.element.popoverContainer.classList.add(POPOVER_ON_RIGHT_EDGE_CLASS);
        } else {
            this.element.popoverContainer.classList.add(POPOVER_IN_CENTER);
        }
    }

    private adjustVerticalOffset(): void {
        if (this.isOnTopEdge()) {
            this.element.popoverContainer.classList.add(POPOVER_BELOW_CLASS);
        } else {
            this.element.popoverContainer.classList.add(POPOVER_ABOVE_CLASS);
        }
    }

    private isOnLeftEdge(): boolean {
        return this.distanceToLeftEdge() < this.minimumDistanceToEdge();
    }

    private isOnRightEdge(): boolean {
        return this.distanceToRightEdge() < this.minimumDistanceToEdge();
    }

    private minimumDistanceToEdge(): number {
        return this.getAdditionalPadding() + this.getPopoverWidth() * 0.5;
    }

    private isOnTopEdge(): boolean {
        return this.distanceToTopEdge() - this.getPopoverHeight() - POPOVER_ARROW_HEIGHT <= 0;
    }

    private distanceToLeftEdge(): number {
        return this.leftOffset();
    }

    private distanceToRightEdge(): number {
        return this.page.viewportWidth() - this.leftOffset();
    }

    private leftOffset(): number {
        return this.element.managingParent!.getAnchorElement().leftOffset();
    }

    private distanceToTopEdge(): number {
        return this.element.managingParent!.getAnchorElement().topOffset() - this.page.getEffectiveYScrollPosition();
    }

    private getPopoverWidth(): number {
        return this.popoverWidth;
    }

    private setPopoverWidth(): void {
        this.popoverWidth = this.element.popoverElement.clientWidth;
    }

    private getPopoverHeight(): number {
        return this.popoverHeight;
    }

    private setPopoverHeight(): void {
        this.popoverHeight = this.element.popoverElement.clientHeight;
        this.element.popoverContainer.style.setProperty("--popover-height", `${this.getPopoverHeight()}px`);
    }

    private getAdditionalPadding(): number {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            return ADDITIONAL_TOOLTIP_PADDING_MOBILE;
        }
        return ADDITIONAL_TOOLTIP_PADDING_DESKTOP;
    }

    private setAdditionalPadding(): void {
        if (this.resolution.upTo(BOOTSTRAP.XS)) {
            this.element.popoverContainer.style.setProperty("--additional-padding", `${ADDITIONAL_TOOLTIP_PADDING_MOBILE}px`);
        } else {
            this.element.popoverContainer.style.setProperty("--additional-padding", `${ADDITIONAL_TOOLTIP_PADDING_DESKTOP}px`);
        }
    }

}